/**
 * FocusRig — eases the camera to frame a set of parts (~0.8 s).
 *
 * Reads live world boxes from the scene's partsApiRef every frame while
 * animating, so framing tracks parts that are still exploding outwards. Keeps
 * the current view direction (only distance and target change), clamps to the
 * OrbitControls distance limits, and hands control straight back to the user:
 * any drag/zoom ('start' event) cancels the ease. No React state per frame;
 * invalidates only while moving.
 *
 *   request     { ids: string[], nonce }  a new object starts a new ease
 *   insetRight  px of the stage covered by a DOM card on the right; the parts
 *               are framed in the remaining width, not behind the card
 */

import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';

const _box = new THREE.Box3();
const _part = new THREE.Box3();
const _centre = new THREE.Vector3();
const _size = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _pos = new THREE.Vector3();
const _right = new THREE.Vector3();
const _target = new THREE.Vector3();
const UP = new THREE.Vector3(0, 1, 0);

const easeInOutCubic = (p) => (p < 0.5 ? 4 * p * p * p : 1 - ((-2 * p + 2) ** 3) / 2);

export default function FocusRig({
  request, partsApiRef, controlsRef, minDistance = 1.2, maxDistance = 100, duration = 0.8,
  insetRight = 0,
}) {
  const camera = useThree((s) => s.camera);
  const invalidate = useThree((s) => s.invalidate);
  const anim = useRef(null);

  useEffect(() => {
    if (!request?.ids?.length) return undefined;
    const controls = controlsRef.current;
    anim.current = {
      ids: request.ids,
      elapsed: 0,
      waited: 0,
      fromPos: camera.position.clone(),
      fromTarget: controls ? controls.target.clone() : new THREE.Vector3(),
    };
    invalidate();
    const cancel = () => { anim.current = null; };
    controls?.addEventListener?.('start', cancel);
    return () => controls?.removeEventListener?.('start', cancel);
  }, [request, camera, controlsRef, invalidate]);

  useFrame((state, delta) => {
    const a = anim.current;
    if (!a) return;
    const dt = Math.min(delta, 1 / 20);
    const api = partsApiRef.current;
    const controls = controlsRef.current;
    if (!api || !controls) {
      // GLB still loading: wait (bounded) rather than framing nothing.
      a.waited += dt;
      if (a.waited > 8) anim.current = null; else invalidate();
      return;
    }

    _box.makeEmpty();
    for (const id of a.ids) if (api.boxOf(id, _part)) _box.union(_part);
    if (_box.isEmpty()) { anim.current = null; return; }

    _box.getCenter(_centre);
    const radius = Math.max(_box.getSize(_size).length() / 2, 0.25);
    const { width, height } = state.size;
    const inset = Math.min(Math.max(insetRight, 0), width * 0.5);
    const vfov = THREE.MathUtils.degToRad(camera.fov ?? 42);
    const hfov = 2 * Math.atan(Math.tan(vfov / 2) * ((width - inset) / Math.max(height, 1)));
    const half = Math.min(vfov, hfov) / 2;
    const dist = THREE.MathUtils.clamp((radius / Math.sin(half)) * 1.15, minDistance, maxDistance);

    _dir.subVectors(a.fromPos, a.fromTarget);
    if (_dir.lengthSq() < 1e-8) _dir.set(1, 0.6, 1);
    _dir.normalize();
    // Shift the orbit target right by half the inset so the parts sit centred
    // in the uncovered part of the stage.
    _right.crossVectors(UP, _dir).normalize();
    const worldPerPx = (2 * dist * Math.tan(vfov / 2)) / Math.max(height, 1);
    _target.copy(_centre).addScaledVector(_right, (inset / 2) * worldPerPx);
    _pos.copy(_target).addScaledVector(_dir, dist);

    a.elapsed += dt;
    const p = Math.min(1, a.elapsed / duration);
    const e = easeInOutCubic(p);
    controls.target.lerpVectors(a.fromTarget, _target, e);
    camera.position.lerpVectors(a.fromPos, _pos, e);
    controls.update();

    if (p >= 1 && api.isSettled()) anim.current = null;
    invalidate();
  });

  return null;
}
